import { useMutation } from "@tanstack/react-query";
import { App } from "antd";
import { type MouseEvent, useCallback } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useNotification } from "~hooks/useNotification";
import type { DefaultOperationSuccessResponse } from "~types/response";

type UserData = {
  userId: number;
  isPendingUser: boolean;
  permissionIds: number[] | null;
};

const useInviteNotifyUser = () => {
  const { modal } = App.useApp();
  const { sport, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const request = useApi();
  const notification = useNotification();

  const { mutateAsync: inviteNotifyUser } = useMutation<DefaultOperationSuccessResponse, Error, UserData>({
    mutationFn: async ({ userId, isPendingUser, permissionIds }) => {
      const url = `${sport}/o/${organizationId}/users/${userId}/${isPendingUser ? "invite" : "notify"}`;
      return await request(url, { method: "POST", body: { permissionIds } });
    },
    onSuccess: (response: DefaultOperationSuccessResponse, { isPendingUser }) => {
      if (response?.data?.[0]?.success) {
        notification.success({
          message: formatMessage({
            id: `permissions.user.actions.${isPendingUser ? "invite" : "notify"}.success`,
          }),
          placement: "bottomRight",
        });
      }
    },
  });

  return useCallback(
    (event: MouseEvent, userId: number, isPendingUser: boolean, permissionIds: number[] | null) => {
      event.preventDefault();
      event.stopPropagation();

      const actionType = isPendingUser ? "invite" : "notify";
      modal.confirm({
        title: formatMessage({ id: `permissions.user.actions.${actionType}` }),
        content: formatMessage({ id: `permissions.user.actions.${actionType}.content` }),
        okText: formatMessage({ id: actionType }),
        onOk: async () => inviteNotifyUser({ userId, isPendingUser, permissionIds }),
        centered: true,
      });
    },
    [formatMessage, modal, inviteNotifyUser],
  );
};

export default useInviteNotifyUser;
